import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowUp, ChevronDown, Copy, Heading, ListChecks, Loader2, RotateCcw, Sparkles, Wand2, X,
} from 'lucide-react';
import { TIPOS, duplicarItem } from '../../../lib/formularios';
import { AuroraLogo } from '../../office/icons';
import { markdownToHtml } from '../../office/markdown';
import '../../office/office.css';
import { ICONOS_TIPO, useEditor } from './contexto';
import { errorAurora, pedirAurora } from './aurora';

/* Cada botón de la barra es una acción del backend (ver email/claude/formularios.md). `pide` abre el
   campo de texto para que la persona diga sobre qué, como en Orvian Docs. */
const BOTONES = [
  { id: 'preguntas', etiqueta: 'Sugerir preguntas', Icono: Sparkles, pide: '¿Sobre qué quieres preguntar?' },
  { id: 'mejorar', etiqueta: 'Mejorar la redacción', Icono: Wand2 },
  { id: 'cabecera', etiqueta: 'Título y descripción', Icono: Heading },
  { id: 'revisar', etiqueta: 'Revisar el formulario', Icono: ListChecks },
];

const TEXTO_OCUPADO = {
  preguntas: 'Aurora IA está pensando preguntas',
  mejorar: 'Aurora IA está revisando la redacción',
  cabecera: 'Aurora IA está escribiendo el título',
  revisar: 'Aurora IA está revisando el formulario',
};

function Seleccionable({ marcada, onMarcar, children }) {
  return (
    <label className="fe-aurora-fila">
      <input type="checkbox" checked={marcada} onChange={onMarcar} />
      {children}
    </label>
  );
}

/**
 * Barra de Aurora IA del editor de formularios: la misma de Orvian Docs (clases `zo-composer*`),
 * abajo y al centro. Aurora propone y aquí se elige qué entra: nada se guarda hasta pulsar el botón,
 * y lo que entra es una edición normal del editor (se deshace con Ctrl+Z).
 */
export default function BarraAurora({ formId, movil }) {
  const ed = useEditor();
  const [abierta, setAbierta] = useState(false);
  const [modo, setModo] = useState(null); // botón que espera texto
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [elegidos, setElegidos] = useState(new Set());
  const entradaRef = useRef(null);
  const ultima = useRef(null);

  const correr = async (accion, prompt = '') => {
    ultima.current = { accion, prompt };
    setAbierta(true);
    setOcupado(accion);
    setResultado(null);
    try {
      const res = await pedirAurora(formId, { accion, prompt });
      if (res.tipo === 'items' && !(res.items || []).length) {
        toast.message('Aurora IA no sugirió preguntas. Prueba a describirlo de otra forma.');
        return;
      }
      if (res.tipo === 'cambios' && !(res.cambios || []).length) {
        toast.success('Aurora IA no vio nada que mejorar en la redacción');
        return;
      }
      setResultado(res);
      if (res.tipo === 'items') setElegidos(new Set((res.items || []).map((it) => it.id)));
      if (res.tipo === 'cambios') setElegidos(new Set((res.cambios || []).map((c) => c.id)));
    } catch (err) {
      toast.error(errorAurora(err));
    } finally {
      setOcupado(null);
    }
  };

  const marcar = (id) => setElegidos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const enviar = (e) => {
    e.preventDefault();
    const prompt = texto.trim();
    if (!prompt || ocupado) return;
    setTexto('');
    correr(modo?.id || 'preguntas', prompt);
  };

  const cerrarResultado = () => {
    setResultado(null);
    setModo(null);
  };

  const anadirPreguntas = () => {
    // `duplicarItem` les da ids nuevos: si se piden dos veces, no chocan con las que ya entraron
    const items = (resultado.items || []).filter((it) => elegidos.has(it.id)).map(duplicarItem);
    if (!items.length) return;
    ed.agregarItems(items);
    toast.success(`${items.length} ${items.length === 1 ? 'pregunta añadida' : 'preguntas añadidas'}`);
    cerrarResultado();
  };

  const aplicarCambios = () => {
    const cambios = (resultado.cambios || []).filter((c) => elegidos.has(c.id));
    if (!cambios.length) return;
    cambios.forEach(({ id, titulo, descripcion }) => {
      const campos = {};
      if (titulo !== undefined) campos.titulo = titulo;
      if (descripcion !== undefined) campos.descripcion = descripcion;
      ed.cambiarItem(id, campos, { clave: `aurora:${id}:${Date.now()}` });
    });
    toast.success(`${cambios.length} ${cambios.length === 1 ? 'pregunta actualizada' : 'preguntas actualizadas'}`, {
      description: 'Puedes deshacerlo con Ctrl+Z.',
    });
    cerrarResultado();
  };

  const aplicarCabecera = () => {
    ed.setCampo('titulo', resultado.titulo, { clave: 'aurora:titulo' });
    if (resultado.descripcion) ed.setCampo('descripcion', resultado.descripcion, { clave: 'aurora:descripcion' });
    toast.success('Título y descripción actualizados', { description: 'Puedes deshacerlo con Ctrl+Z.' });
    cerrarResultado();
  };

  const copiar = async (valor) => {
    try {
      await navigator.clipboard.writeText(valor);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  useEffect(() => {
    if (!abierta) return undefined;
    const alTeclear = (e) => { if (e.key === 'Escape' && !resultado) setAbierta(false); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierta, resultado]);

  if (!abierta) {
    return (
      <div className={`zo-composer fe-barra-aurora ${movil ? 'is-movil' : ''}`}>
        <button
          type="button"
          className="zo-composer-pill"
          onClick={() => setAbierta(true)}
          data-testid="fe-aurora-abrir"
        >
          <AuroraLogo size={16} /> Crear con Aurora IA
        </button>
      </div>
    );
  }

  return (
    <div className={`zo-composer is-open fe-barra-aurora ${movil ? 'is-movil' : ''}`} data-testid="fe-barra-aurora">
      {ocupado && (
        <div className="zo-ai-card zo-ai-thinking">
          <AuroraLogo size={18} />
          <span>{TEXTO_OCUPADO[ocupado] || 'Aurora IA está pensando'}…</span>
          <div className="zo-shimmer" />
        </div>
      )}

      {resultado && !ocupado && (
        <div className="zo-ai-card" data-testid="fe-aurora-resultado">
          <div className="zo-ai-card-body">
            {resultado.tipo === 'items' && (
              <>
                <p className="zo-muted">Elige qué preguntas añadir:</p>
                {(resultado.items || []).map((it) => {
                  const Icono = ICONOS_TIPO[it.tipo];
                  return (
                    <Seleccionable key={it.id} marcada={elegidos.has(it.id)} onMarcar={() => marcar(it.id)}>
                      {Icono && <Icono className="fe-aurora-icono" />}
                      <span className="fe-aurora-texto">
                        <strong>{it.titulo || 'Sin título'}</strong>
                        <small>
                          {TIPOS[it.tipo]?.nombre}
                          {it.opciones?.length ? ` · ${it.opciones.map((o) => o.texto).join(', ')}` : ''}
                        </small>
                      </span>
                    </Seleccionable>
                  );
                })}
              </>
            )}

            {resultado.tipo === 'cambios' && (
              <>
                <p className="zo-muted">Redacción propuesta (se cambia solo el texto, no el tipo ni las opciones):</p>
                {(resultado.cambios || []).map((c) => (
                  <Seleccionable key={c.id} marcada={elegidos.has(c.id)} onMarcar={() => marcar(c.id)}>
                    <span className="fe-aurora-texto">
                      <small className="fe-aurora-antes">{c.antes || 'Sin título'}</small>
                      <strong>{c.titulo || c.antes}</strong>
                      {c.descripcion !== undefined && <small>{c.descripcion || '(sin descripción)'}</small>}
                    </span>
                  </Seleccionable>
                ))}
              </>
            )}

            {resultado.tipo === 'cabecera' && (
              <div className="fe-aurora-cabecera">
                <strong>{resultado.titulo}</strong>
                {/* La descripción viene saneada del backend (mismo saneador que el editor) */}
                {resultado.descripcion && (
                  <div className="fm-html" dangerouslySetInnerHTML={{ __html: resultado.descripcion }} />
                )}
              </div>
            )}

            {resultado.tipo === 'texto' && (
              // markdownToHtml escapa todo el texto antes de agregar etiquetas
              <div className="zo-md" dangerouslySetInnerHTML={{ __html: markdownToHtml(resultado.texto || '') }} />
            )}
          </div>

          <div className="zo-ai-card-actions">
            {resultado.tipo === 'items' && (
              <button type="button" className="zo-btn-primary" onClick={anadirPreguntas} disabled={!elegidos.size}>
                Añadir {elegidos.size ? `(${elegidos.size})` : ''}
              </button>
            )}
            {resultado.tipo === 'cambios' && (
              <button type="button" className="zo-btn-primary" onClick={aplicarCambios} disabled={!elegidos.size}>
                Aplicar {elegidos.size ? `(${elegidos.size})` : ''}
              </button>
            )}
            {resultado.tipo === 'cabecera' && (
              <button type="button" className="zo-btn-primary" onClick={aplicarCabecera}>Aplicar</button>
            )}
            <span className="zo-spacer" />
            {resultado.tipo === 'texto' && (
              <button type="button" className="zo-icon-btn" title="Copiar" onClick={() => copiar(resultado.texto || '')}>
                <Copy className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              className="zo-icon-btn"
              title="Volver a generar"
              onClick={() => ultima.current && correr(ultima.current.accion, ultima.current.prompt)}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <button type="button" className="zo-icon-btn" title="Descartar" onClick={cerrarResultado}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {!resultado && !ocupado && (
        <div className="zo-chips">
          {BOTONES.map(({ id, etiqueta, Icono, pide }) => (
            <button
              key={id}
              type="button"
              className={`zo-chip ${modo?.id === id ? 'is-active' : ''}`}
              onClick={() => {
                if (pide) {
                  setModo({ id, etiqueta, pide });
                  setTimeout(() => entradaRef.current?.focus(), 50);
                } else {
                  correr(id);
                }
              }}
            >
              <Icono className="h-4 w-4" /> {etiqueta}
            </button>
          ))}
        </div>
      )}

      <form className="zo-composer-bar" onSubmit={enviar}>
        <AuroraLogo size={18} />
        {modo && (
          <span className="zo-mode-tag">
            {modo.etiqueta}
            <button type="button" aria-label="Quitar" onClick={() => setModo(null)}><X className="h-3 w-3" /></button>
          </span>
        )}
        <input
          ref={entradaRef}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={modo?.pide || 'Pide preguntas a Aurora IA… (por ejemplo: datos del visitante y motivo)'}
          aria-label="Instrucción para Aurora IA"
        />
        <button type="button" className="zo-icon-btn" title="Ocultar" onClick={() => setAbierta(false)}>
          <ChevronDown className="h-4 w-4" />
        </button>
        <button type="submit" className="zo-send" disabled={!texto.trim() || !!ocupado} aria-label="Enviar">
          {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
